import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Eye, 
  Loader2,
  ArrowUp,
  ExternalLink,
  Building2,
  X,
  Users
} from 'lucide-react';
import api from '../../lib/api';
import SearchField from '../SearchField';
import RecordCount from '../RecordCount';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import StatCard from '../StatCard';
import { LOBBYING_API_TIMEOUT_MS } from './constants';
import { createLobbyingBillFilterUrl } from '../../utils/billStatusMapping';
import ViewToggle from '../ViewToggle';

interface ClientSummary {
  id: string;
  client_name: string;
  lobbyist_count: number;
  bill_count: number;
  lobbyist_names: string;
}

interface ClientsSummaryResponse {
  clients: ClientSummary[];
  total: number;
  returned_count: number;
  has_more: boolean;
  offset: number;
  limit: number;
}

interface AlphabetIndexItem {
  letter: string;
  client_count?: number;
  total_count?: number;
}

type SortField = 'name' | 'lobbyist_count' | 'bill_count';
type SortDirection = 'asc' | 'desc';

const ClientsTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'starts_with' | 'contains'>('starts_with');
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreClients, setHasMoreClients] = useState(false);
  const [alphabetIndex, setAlphabetIndex] = useState<AlphabetIndexItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('current'); // Default to current lobbyists
  
  // Lobbyist filter from URL params
  const [lobbyistFilter, setLobbyistFilter] = useState<{id: string, name: string} | null>(null);
  
  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Sorting state
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const fetchRequestIdRef = useRef(0);
  const listLengthRef = useRef(0);

  listLengthRef.current = clients.length;

  // Fetch clients with aggregated counts (progressive loading)
  const fetchClients = useCallback(async (isInitialLoad = true) => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      if (isInitialLoad) {
        setLoading(true);
        setClients([]);
        setTotalCount(0);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        sortBy: sortBy,
        sortDirection: sortDirection,
        search_mode: searchMode,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && statusFilter !== 'all' && { lobbyist_status: statusFilter }),
        ...(lobbyistFilter && { lobbyist_id: lobbyistFilter.id }),
        limit: isInitialLoad ? '100' : '1000',
        offset: isInitialLoad ? '0' : listLengthRef.current.toString(),
        get_total: 'true'
      });

      const response = await api.get(`/lobbying/clients-summary?${params}`, {
        timeout: LOBBYING_API_TIMEOUT_MS,
      });

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      
      if (response.data.success) {
        const data: ClientsSummaryResponse = response.data.data;
        
        if (isInitialLoad) {
          setClients(data.clients);
          setTotalCount(data.total || data.clients.length);
          setHasMoreClients(data.has_more || false);
          
          // DISABLED: Background loading completely disabled to prevent count issues
          // Users can use "Load More" button when needed
        } else {
          // Append additional clients to existing list
          setClients(prev => [...prev, ...data.clients]);
          setTotalCount(data.total ?? listLengthRef.current + data.clients.length);
          setHasMoreClients(data.has_more || false);
        }
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      console.error('Error fetching clients summary:', error);
      if (isInitialLoad) {
        setClients([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  }, [searchQuery, searchMode, statusFilter, lobbyistFilter, sortBy, sortDirection]);

  // Fetch alphabet index
  const fetchAlphabetIndex = async () => {
    try {
      const response = await api.get('/lobbying/clients-alphabet-index');
      
      if (response.data.success) {
        // Group numeric letters into "0-9"
        const processedData = [];
        let numericCount = 0;
        
        for (const item of response.data.data) {
          if (/^\d/.test(item.letter)) {
            numericCount += item.client_count || 0;
          } else {
            processedData.push(item);
          }
        }
        
        // Add "0-9" group if there are numeric entries
        if (numericCount > 0) {
          processedData.unshift({
            letter: '0-9',
            client_count: numericCount,
            total_count: numericCount
          });
        }
        
        // Sort: 0-9 first, then alphabetically
        processedData.sort((a, b) => {
          if (a.letter === '0-9') return -1;
          if (b.letter === '0-9') return 1;
          return a.letter.localeCompare(b.letter);
        });
        
        setAlphabetIndex(processedData);
      }
    } catch (error) {
      console.error('Error fetching alphabet index:', error);
      setAlphabetIndex([]);
    }
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const sortField = field as SortField;
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('asc');
    }
  };

  // Navigate to filtered legislation page for this client row (id = name + zip)
  const handleBillCountClick = (clientId: string, clientName: string) => {
    navigate(
      createLobbyingBillFilterUrl({
        clientId,
        clientName,
        ...(statusFilter && statusFilter !== 'all' && { lobbyistStatus: statusFilter }),
        ...(lobbyistFilter && {
          lobbyistId: lobbyistFilter.id,
          lobbyistName: lobbyistFilter.name,
        }),
      })
    );
  };

  // Handle alphabet filter
  const handleAlphabetFilter = (letter: string) => {
    setSearchQuery(letter);
    setSearchMode('starts_with');
  };

  // Load more clients function
  const loadMoreClients = useCallback(() => {
    if (!loadingMore && hasMoreClients) {
      fetchClients(false);
    }
  }, [loadingMore, hasMoreClients, fetchClients]);


  // Scroll to top functionality
  const scrollToTop = () => {
    // Scroll both window and main container to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Also scroll the main container if it exists and is scrolled
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0 && container.scrollTop > 0) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  // Read URL parameters for lobbyist filter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const lobbyistId = urlParams.get('lobbyist_id');
    const lobbyistName = urlParams.get('lobbyist_name');
    
    if (lobbyistId && lobbyistName) {
      setLobbyistFilter({ id: lobbyistId, name: lobbyistName });
    } else {
      setLobbyistFilter(null);
    }
  }, [location.search]);

  useEffect(() => {
    fetchAlphabetIndex();
  }, []);

  // Single fetch path — debounce typing only (avoid duplicate mount + debounced requests)
  useEffect(() => {
    const delay = searchQuery.trim() ? 300 : 0;
    const timeoutId = window.setTimeout(() => {
      fetchClients(true);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchMode, statusFilter, lobbyistFilter, sortBy, sortDirection, fetchClients]);

  // Handle scroll event to show/hide scroll to top button and infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      let mainContainer = null;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = Math.max(containerScrollTop, container.scrollTop);
          mainContainer = container;
        }
      }
      
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px

      // Infinite scroll detection
      if (hasMoreClients && !loadingMore) {
        let scrollThreshold;
        
        if (mainContainer) {
          // Check if we're near the bottom of the main container
          const containerBottom = mainContainer.scrollHeight - mainContainer.scrollTop - mainContainer.clientHeight;
          scrollThreshold = containerBottom < 200; // Trigger when 200px from bottom
        } else {
          // Fallback to window scroll detection
          const windowBottom = document.documentElement.scrollHeight - window.innerHeight - windowScrollTop;
          scrollThreshold = windowBottom < 200;
        }
        
        if (scrollThreshold) {
          loadMoreClients();
        }
      }
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after clients load
    let retryTimer: number | null = null;
    if (!mainContainer && clients.length === 0) {
      retryTimer = window.setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    // Force show button if page is tall and has many clients
    if (clients.length >= 50) {
      const checkPageHeight = () => {
        const bodyHeight = document.body.scrollHeight;
        const windowHeight = window.innerHeight;
        if (bodyHeight > windowHeight * 2) {
          handleScroll();
        }
      };
      setTimeout(checkPageHeight, 100);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [clients, hasMoreClients, loadingMore, loadMoreClients]); // Re-run when clients change or infinite scroll state changes

  // Parse lobbyist names and truncate if needed
  const getLobbyistDisplay = (lobbyistNames: string) => {
    if (!lobbyistNames) return '-';
    
    const names = lobbyistNames.split(', ');
    if (names.length <= 2) {
      return names.join(', ');
    }
    
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  const renderCard = (client: ClientSummary) => (
    <div
      key={client.id}
      className="card hover:shadow-lg transition-shadow duration-200"
    >
      <div className="flex items-start mb-4">
        <Building2 className="w-5 h-5 text-primary-600 mr-3 mt-1 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">
            {client.client_name}
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <StatCard label="Lobbyists" value={client.lobbyist_count} />
        <StatCard
          label="Bills"
          value={client.bill_count}
          onClick={() => handleBillCountClick(client.id, client.client_name)}
        />
      </div>

      <div className="mb-4">
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wider mb-2">
          Represented by
        </p>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {getLobbyistDisplay(client.lobbyist_names)}
        </p>
      </div>

      <div className="pt-4 border-t border-border-light dark:border-border-dark">
        <button 
          onClick={(e) => { e.stopPropagation(); handleBillCountClick(client.id, client.client_name); }}
          className="w-full flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          View Bills
        </button>
      </div>
    </div>
  );

  const renderTable = () => (
    <StandardTable>
      <StandardTableHeader>
        <StandardTableRow>
          <StandardTableHeaderCell
            sortable
            sortKey="name"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="auto"
          >
            Client Name
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="lobbyist_count"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
            responsiveHide="sm"
          >
            Lobbyists
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="expand"
            responsiveHide="md"
          >
            Represented by
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="bill_count"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
          >
            Bills
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="actions"
            align="right"
          >
            Actions
          </StandardTableHeaderCell>
        </StandardTableRow>
      </StandardTableHeader>
      <StandardTableBody>
        {clients.map((client) => (
          <StandardTableRow key={client.id} onClick={() => navigate(`/clients/${client.id}`)}>
            <StandardTableCell>
              <div className="flex items-center">
                <Building2 className="w-5 h-5 text-primary-600 mr-3 flex-shrink-0" />
                <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {client.client_name}
                </div>
              </div>
            </StandardTableCell>
            <StandardTableCell responsiveHide="sm">
              {client.lobbyist_count}
            </StandardTableCell>
            <StandardTableCell responsiveHide="md">
              <div className="truncate" title={client.lobbyist_names}>
                {getLobbyistDisplay(client.lobbyist_names)}
              </div>
            </StandardTableCell>
            <StandardTableCell>
              <button
                onClick={(e) => { e.stopPropagation(); handleBillCountClick(client.id, client.client_name); }}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1"
              >
                {client.bill_count}
                <ExternalLink className="w-3 h-3" />
              </button>
            </StandardTableCell>
            <StandardTableCell align="right">
              <button
                onClick={(e) => { e.stopPropagation(); handleBillCountClick(client.id, client.client_name); }}
                className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
                title="View bills"
                aria-label="View bills"
              >
                <Eye className="w-4 h-4" />
              </button>
            </StandardTableCell>
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );

  const showInitialLoader = loading && clients.length === 0;

  return (
    <div className="space-y-6">
      {/* Alphabet Filter */}
      <div className="flex flex-wrap gap-1 p-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
        {/* All button */}
        <button
          onClick={() => setSearchQuery('')}
          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            !searchQuery
              ? 'bg-primary-500 text-white'
              : 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
          }`}
        >
          All
        </button>
        
        {/* Numbers button */}
        {(() => {
          const numbersData = alphabetIndex.find(item => item.letter === '0-9');
          const isActive = searchQuery === '0-9' && searchMode === 'starts_with';
          const hasData = numbersData && (numbersData.client_count ?? 0) > 0;
          
          return (
            <button
              onClick={() => handleAlphabetFilter('0-9')}
              disabled={!hasData}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-500 text-white'
                  : hasData
                  ? 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary cursor-not-allowed'
              }`}
              title={hasData ? `${numbersData?.client_count || 0} clients` : 'No clients'}
            >
              0-9
            </button>
          );
        })()}
        
        {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(letter => {
          const letterData = alphabetIndex.find(item => item.letter === letter);
          const isActive = searchQuery === letter && searchMode === 'starts_with';
          const hasData = letterData && (letterData.client_count ?? 0) > 0;
          
          return (
            <button
              key={letter}
              onClick={() => handleAlphabetFilter(letter)}
              disabled={!hasData}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-500 text-white'
                  : hasData
                  ? 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary cursor-not-allowed'
              }`}
              title={hasData ? `${letterData?.client_count || 0} clients` : 'No clients'}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* Lobbyist Filter Indicator */}
      {lobbyistFilter && (
        <div className="mb-4 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <Users className="w-4 h-4 text-info-600 dark:text-info-400 flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                Filtered by Lobbyist:
              </span>
              <span className="text-sm font-semibold text-info-700 dark:text-info-300">
                {lobbyistFilter.name}
              </span>
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                ({totalCount} {totalCount === 1 ? 'client' : 'clients'})
              </span>
            </div>
            <button
              onClick={() => {
                navigate('/lobbying#clients');
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-info-700 dark:text-info-300 hover:text-info-900 dark:hover:text-info-100 bg-background dark:bg-background-dark-secondary rounded-md transition-colors"
              title="Clear filter"
            >
              <X className="w-4 h-4" />
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
            Lobbyist Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-auto min-w-[140px]"
          >
            <option value="current">Current Only</option>
            <option value="all">All Statuses</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        {/* Search Bar */}
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search clients by name or organization..."
          className="max-w-md"
        />

      </div>

      {/* View Toggle and Results */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <RecordCount
          label="clients"
          showing={clients.length}
          filtered={totalCount}
          total={totalCount}
          isLoading={loading}
        />
      </div>

      {/* Results */}
      {showInitialLoader ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clients.map(renderCard)}
            </div>
          ) : (
            renderTable()
          )}
          
          {/* Load More button when there are more clients */}
          {hasMoreClients && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => fetchClients(false)}
                disabled={loadingMore}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  `Load More Clients (${totalCount - clients.length} remaining)`
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Scroll to top button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

export default ClientsTab;
